import React, { useMemo, useState } from 'react';
import styles from './CreatorsTable.module.css';
import PaginationTable, { PaginationTableProps } from '../../../../../../../ui/PaginationTable/PaginationTable';
import { microwave, TiktokUserImage } from '@round/api';
import { CellProps, Column } from 'react-table';
import { Badge, Checkbox, Image, Skeleton } from '@round/ui-kit';
import {
    buildInstagramUserUrl,
    buildTiktokUserUrl,
    buildYoutubeChannelUrl,
    formatNumberToKNotation,
    numberWithCommas,
} from '../../../../../../../helpers';
import TruncatedTextCell from '../../../../../../../SharedComponents/TableComponents/TruncatedTextCell/TruncatedTextCell';
import { ReactComponent as CheckIcon } from '../../../../../../../assets/Check.svg';
import Rating from '../../../../../../../ui/Rating/Rating';
import Location from './Location/Location';
import Tags from './Tags/Tags';
import cn from 'classnames';
import GenreAffinityCell from '../../../components/GenreAffinityCell/GenreAffinityCell';

export type CreatorsTableRow = microwave.MicrowaveInfluencer & {
    image: TiktokUserImage | undefined;
};

type CreatorsTableColumn = Column<CreatorsTableRow> & {
    isHidden?: boolean;
};

type Props = Omit<PaginationTableProps<CreatorsTableRow>, 'columns'> & {
    selectedRowIds: number[];
    onRowSelect: (row: CreatorsTableRow) => void;
    onAllRowsSelect: (rows: CreatorsTableRow[]) => void;
    onUpdate: (id: number, data: Partial<microwave.MicrowaveInfluencerWrite>) => Promise<void>;
    shouldShowGenreAffinity: boolean;
};

const CreatorsTable = ({
    selectedRowIds,
    onRowSelect,
    onAllRowsSelect,
    onUpdate,
    shouldShowGenreAffinity,
    ...props
}: Props) => {
    const [multiSelectAnchorRowId, setMultiSelectAnchorRowId] = useState<number | null>(null);

    const columns: CreatorsTableColumn[] = useMemo(
        () => [
            {
                Header: () => (
                    <div>
                        <Checkbox
                            ariaLabel="Select all"
                            value={props.data.length > 0 && props.data.every((row) => selectedRowIds.includes(row.id))}
                            onChange={() => onAllRowsSelect(props.data)}
                        />
                    </div>
                ),
                id: 'select',
                disableSortBy: true,
                Cell: ({ row: { original } }: CellProps<CreatorsTableRow>) => {
                    return (
                        <Checkbox
                            className={cn({ [styles.anchor]: original.id === multiSelectAnchorRowId })}
                            value={selectedRowIds.includes(original.id)}
                            onChange={(value, event) => {
                                if (event.shiftKey || event.ctrlKey || event.metaKey) {
                                    return;
                                }

                                onRowSelect(original);
                                if (!selectedRowIds.includes(original.id)) {
                                    setMultiSelectAnchorRowId(original.id);
                                }
                            }}
                        />
                    );
                },
            },
            {
                Header: 'Account',
                accessor: 'tiktok_user_id',
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    const youtubeProfileUrl = original.youtube_channel_youtube_id
                        ? buildYoutubeChannelUrl(original.youtube_channel_youtube_id)
                        : '';

                    return (
                        <div className={styles.account}>
                            <Image
                                className={styles.avatar}
                                src={original.image?.avatar_thumb.cached_url ?? ''}
                                alt={original.tiktok_user_nickname}
                            />
                            <div className={styles.accountDetails}>
                                <div className={styles.nickname}>
                                    {original.tiktok_user_nickname}
                                    {original.tiktok_user_verified && (
                                        <div className={styles.verifiedIcon}>
                                            <CheckIcon />
                                        </div>
                                    )}
                                    {original.private_tiktok_account && (
                                        <Badge className={styles.privateAccountBadge}>private</Badge>
                                    )}
                                </div>
                                {!!original.tiktok_user_unique_id && (
                                    <div>
                                        <b>TT: </b>
                                        <a
                                            className={styles.uniqueId}
                                            onClick={(e) => e.stopPropagation()}
                                            rel="noopener noreferrer"
                                            target="_blank"
                                            href={buildTiktokUserUrl(original.tiktok_user_unique_id)}
                                        >
                                            @{original.tiktok_user_unique_id}
                                        </a>
                                    </div>
                                )}

                                {!!original.instagram_user_username && (
                                    <div>
                                        <b>IG: </b>
                                        <a
                                            className={styles.uniqueId}
                                            onClick={(e) => e.stopPropagation()}
                                            rel="noopener noreferrer"
                                            target="_blank"
                                            href={buildInstagramUserUrl(original.instagram_user_username)}
                                        >
                                            @{original.instagram_user_username}
                                        </a>
                                    </div>
                                )}

                                {!!original.youtube_channel_title && (
                                    <div>
                                        <b>YT: </b>
                                        <a
                                            className={styles.uniqueId}
                                            onClick={(e) => e.stopPropagation()}
                                            rel="noopener noreferrer"
                                            target="_blank"
                                            href={youtubeProfileUrl}
                                        >
                                            {original.youtube_channel_title}
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                },
            },
            {
                Header: 'Follower count',
                accessor: 'follower_count',
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return <>{formatNumberToKNotation(value)}</>;
                },
            },
            {
                Header: 'Location',
                accessor: 'location',
                Cell: ({ value, row: { original } }) => {
                    const [isUpdating, setIsUpdating] = useState(false);
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return (
                        <Location
                            location={value}
                            onChange={(location) => {
                                if (location === value) {
                                    return;
                                }
                                setIsUpdating(true);
                                onUpdate(original.id, { location }).finally(() => setIsUpdating(false));
                            }}
                            isDisabled={isUpdating}
                            isClearable
                        />
                    );
                },
            },
            {
                Header: 'Tags',
                accessor: 'content_tags',
                disableSortBy: true,
                Cell: (cellProps) => (
                    <Tags
                        {...cellProps}
                        isLoading={props.loading}
                        onChange={async (influencerId, tagIds) => {
                            onUpdate(influencerId, { content_tags: tagIds });
                        }}
                    />
                ),
            },
            {
                Header: 'Signature',
                accessor: 'tiktok_signature',
                disableSortBy: true,
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return <TruncatedTextCell>{value}</TruncatedTextCell>;
                },
            },
            {
                Header: 'Genre Affinity',
                accessor: 'genre_affinity',
                disableSortBy: true,
                isHidden: !shouldShowGenreAffinity,
                Cell: ({ value: genreAffinityStats }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    if (!genreAffinityStats) {
                        return <>-</>;
                    }

                    return <GenreAffinityCell genreAffinityStats={genreAffinityStats} />;
                },
            },
            {
                Header: 'Avg Micro Post Views',
                accessor: 'tiktok_avg_microwave_post_views',
                disableSortBy: true,
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return <>{value ? numberWithCommas(value) : '-'}</>;
                },
            },
            {
                Header: 'Post Rate',
                accessor: 'tiktok_post_rate',
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return (
                        <>
                            {typeof value === 'string' ? `${numberWithCommas((Number(value) * 100).toFixed(2))}%` : '-'}
                        </>
                    );
                },
            },
            {
                Header: 'Past Posts',
                accessor: 'tiktok_post_count',
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return <>{typeof value === 'number' ? numberWithCommas(value) : '-'}</>;
                },
            },
            {
                Header: 'Past Invites',
                accessor: 'tiktok_post_invite_count',
                disableSortBy: true,
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return <>{typeof value === 'number' ? numberWithCommas(value) : '-'}</>;
                },
            },
            {
                Header: 'Rating',
                accessor: 'rating',
                disableSortBy: true,
                Cell: ({ value, row: { original } }) => {
                    const [isUpdating, setIsUpdating] = useState(false);
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return (
                        <Rating
                            disabled={isUpdating}
                            maxRating={3}
                            rating={value}
                            onChange={(rating) => {
                                setIsUpdating(true);
                                onUpdate(original.id, { rating }).finally(() => setIsUpdating(false));
                            }}
                        />
                    );
                },
            },
            {
                Header: 'Email',
                accessor: 'email',
                disableSortBy: true,
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return <TruncatedTextCell>{value}</TruncatedTextCell>;
                },
            },
        ],
        [
            multiSelectAnchorRowId,
            onAllRowsSelect,
            onRowSelect,
            onUpdate,
            props.data,
            props.loading,
            selectedRowIds,
            shouldShowGenreAffinity,
        ]
    );

    const relevantColumns = useMemo(() => columns.filter((column) => !column.isHidden), [columns]);
    return (
        <PaginationTable
            tableClassName={styles.table}
            columns={relevantColumns}
            {...props}
            onContextMenu={(e) => {
                // MacOS ctrl+click is context menu action
                if (e.ctrlKey) {
                    e.preventDefault();
                }
            }}
            onRowClick={(row, page, event) => {
                if (event.shiftKey && !multiSelectAnchorRowId) {
                    onRowSelect(row);
                    setMultiSelectAnchorRowId(row.id);
                    return;
                }

                if (event.shiftKey) {
                    const anchorIndex = page.findIndex((row) => row.original.id === multiSelectAnchorRowId);
                    const targetIndex = page.findIndex((tableRow) => tableRow.original.id === row.id);

                    const rowsInRange = page.slice(
                        Math.min(anchorIndex, targetIndex),
                        Math.max(anchorIndex, targetIndex) + 1
                    );

                    rowsInRange.forEach((row) => {
                        if (!selectedRowIds.includes(row.original.id)) {
                            onRowSelect(row.original);
                        }
                    });
                    return;
                }

                if (event.ctrlKey || event.metaKey) {
                    onRowSelect(row);
                    setMultiSelectAnchorRowId(row.id);
                }
            }}
        />
    );
};

export default CreatorsTable;
